import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import './Instructions.css';

function Instructions() {
  return (
    <div className="instructions-page">
      <Header />

      <main className="instructions-main">
        <div className="instructions-container">
          <h1>How to Use Rec-Chord</h1>

          <nav className="instructions-nav">
            <ul>
              <li><a href="#getting-started">Getting Started</a></li>
              <li><a href="#uploading-audio">Uploading Audio</a></li>
              <li><a href="#transcription">Transcription Process</a></li>
              <li><a href="#editing">Editing Transcripts</a></li>
              <li><a href="#exporting">Exporting Subtitles</a></li>
              <li><a href="#version-history">Version History</a></li>
              <li><a href="#sharing">Sharing & Collaboration</a></li>
              <li><a href="#keyboard-shortcuts">Keyboard Shortcuts</a></li>
              <li><a href="#troubleshooting">Troubleshooting</a></li>
            </ul>
          </nav>

          <section id="getting-started" className="instruction-section">
            <h2>Getting Started</h2>
            <div className="step-by-step">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Create Your Account</h3>
                  <p>Sign up with your email or use Google SSO for quick access. You'll need an active subscription to transcribe audio files.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Set Up Payment</h3>
                  <p>Add a payment method to activate your account. This allows unlimited transcriptions and access to all features.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Upload Your First Audio</h3>
                  <p>Drag and drop an audio file or click to browse. Supported formats include MP3, WAV, M4A, and more.</p>
                </div>
              </div>
            </div>
          </section>

          <section id="uploading-audio" className="instruction-section">
            <h2>Uploading Audio</h2>
            <div className="feature-grid">
              <div className="feature-card">
                <h3>Supported Audio Formats</h3>
                <ul>
                  <li>MP3 - Most common format</li>
                  <li>WAV - High quality, uncompressed</li>
                  <li>M4A - Apple/iTunes format</li>
                  <li>FLAC - Lossless compression</li>
                  <li>OGG - Open source format</li>
                  <li>AAC, OPUS, and more</li>
                </ul>
              </div>
              <div className="feature-card">
                <h3>Audio Extraction from Video</h3>
                <ul>
                  <li>MP4, MOV, AVI, MKV, WebM supported</li>
                  <li>Audio track extracted automatically</li>
                  <li>Quick conversion (few seconds)</li>
                  <li>Same accuracy as direct audio</li>
                  <li>Progress shown in real-time</li>
                  <li>Original video not stored</li>
                </ul>
              </div>
              <div className="feature-card">
                <h3>File Requirements</h3>
                <ul>
                  <li>Max audio size: 1GB</li>
                  <li>Max video size: 2GB (for audio extraction)</li>
                  <li>Minimum quality: 16kHz sample rate</li>
                  <li>Best results: Clear speech, minimal background noise</li>
                  <li>Language: Optimized for English</li>
                </ul>
              </div>
              <div className="feature-card">
                <h3>Upload Methods</h3>
                <ul>
                  <li><strong>Drag & Drop:</strong> Drag audio files directly</li>
                  <li><strong>Click to Browse:</strong> Click the upload area to select files</li>
                  <li><strong>Record Live:</strong> Use the built-in recorder for quick captures</li>
                  <li><strong>Video Files:</strong> Audio extracted automatically</li>
                </ul>
              </div>
            </div>
            <div className="feature-note" style={{ marginTop: '20px', padding: '15px', background: 'var(--color-background-secondary)', borderRadius: '8px' }}>
              <h4>Video Audio Extraction</h4>
              <p>When you upload a video file (MP4, MOV, etc.), we automatically extract the audio track for transcription. This takes just a few seconds and you can watch the progress in real-time. The extracted audio is transcribed with the same accuracy as direct audio uploads. Note: Only the audio is processed — we don't store or transcribe visual content.</p>
            </div>
          </section>

          <section id="transcription" className="instruction-section">
            <h2>Transcription Process</h2>
            <div className="process-flow">
              <div className="process-step">
                <h3>1. Automatic Processing</h3>
                <p>Our AI analyzes your audio using advanced speech recognition technology. This typically takes 10-30% of your audio length.</p>
              </div>
              <div className="process-step">
                <h3>2. Confidence Analysis</h3>
                <p>Each word receives a confidence score:</p>
                <ul>
                  <li><span className="confidence-high">High (>=80%)</span> - Highlighted with your configured high-confidence color</li>
                  <li><span className="confidence-medium">Medium (50-79%)</span> - Highlighted with your configured medium-confidence color</li>
                  <li><span className="confidence-low">Low (&lt;50%)</span> - Highlighted with your configured low-confidence color</li>
                </ul>
                <p style={{ marginTop: '10px', fontSize: '0.9rem', opacity: 0.9 }}>You can customize confidence threshold percentages in the transcript settings.</p>
              </div>              <div className="process-step">
                <h3>3. Word-Level Timestamps</h3>
                <p>Every word is precisely timed to your audio, enabling click-to-seek functionality and perfect synchronization.</p>
              </div>
            </div>
          </section>

          <section id="editing" className="instruction-section">
            <h2>Editing Transcripts</h2>
            
            <div className="editing-modes">
              <div className="mode-card">
                <h3>Listening Mode (Default)</h3>
                <ul>
                  <li><strong>Click words:</strong> Jump to that point in audio</li>
                  <li><strong>Auto-scroll:</strong> Transcript follows audio playback</li>
                  <li><strong>Confidence Counter:</strong> Shows count of high/medium/low confidence words</li>
                  <li><strong>Bin Navigation:</strong> Enable in settings to use arrow keys for navigating within confidence bins</li>
                </ul>
              </div>

              <div className="mode-card">
                <h3>Typing Mode</h3>
                <p>Enable advanced editing capabilities:</p>
                <ul>
                  <li><strong>Text editing:</strong> Type to modify words directly</li>
                  <li><strong>Word navigation:</strong> Arrow keys move between words</li>
                  <li><strong>Ctrl + Arrow:</strong> Skip to next non-ghosted word</li>
                  <li><strong>Space:</strong> Split words or insert new ones</li>
                  <li><strong>Backspace/Delete:</strong> Remove characters or delete entire words</li>
                  <li><strong>Enter:</strong> Insert new word after current selection</li>
                </ul>
              </div>

              <div className="mode-card">
                <h3>Click-to-Delete Mode</h3>
                <p>Quick deletion mode for cleaning up transcripts:</p>
                <ul>
                  <li><strong>Click any word:</strong> Immediately mark it as deleted</li>
                  <li><strong>Toggle visibility:</strong> Use "Hide Deleted Words" setting to hide/show deleted words</li>
                  <li><strong>Works independently:</strong> Can be used with or without typing mode</li>
                  <li><strong>Non-destructive:</strong> Deleted words are ghosted, not removed from data</li>
                </ul>
              </div>
            </div>

            <div className="word-states">
              <h3>Understanding Word States</h3>
              <div className="state-examples">
                <div className="state-example">
                  <span className="word-example active-word">Active</span>
                  <p>Currently playing word (blue border with pulse)</p>
                </div>
                <div className="state-example">
                  <span className="word-example selected-word">Selected</span>
                  <p>Selected for editing (green border)</p>
                </div>
                <div className="state-example">
                  <span className="word-example editing-word">Editing</span>
                  <p>Currently being edited (orange border with cursor)</p>
                </div>
                <div className="state-example">
                  <span className="word-example inserted-word">NEW</span>
                  <p>User-inserted word (purple background)</p>
                </div>
                <div className="state-example">
                  <span className="word-example ghosted-word">Ghosted</span>
                  <p>Deleted word (strikethrough, low opacity)</p>
                </div>
              </div>
            </div>
          </section>

          <section id="exporting" className="instruction-section">
            <h2>Exporting Subtitles</h2>

            <p style={{ marginBottom: '20px' }}>Export your transcripts as subtitles for use with video players, YouTube, streaming platforms, and accessibility compliance.</p>

            <div className="feature-grid">
              <div className="feature-card">
                <h3>SRT (SubRip)</h3>
                <ul>
                  <li><strong>Best for:</strong> Universal compatibility</li>
                  <li>Supported by virtually all video players</li>
                  <li>Simple, reliable format</li>
                  <li>Perfect for video editing software</li>
                  <li>Works with VLC, Media Player, etc.</li>
                </ul>
              </div>

              <div className="feature-card">
                <h3>VTT (WebVTT)</h3>
                <ul>
                  <li><strong>Best for:</strong> Web and YouTube</li>
                  <li>HTML5 video standard format</li>
                  <li>YouTube native format</li>
                  <li>Web player compatible</li>
                  <li>Supports advanced styling</li>
                </ul>
              </div>

              <div className="feature-card">
                <h3>ASS (Advanced SubStation)</h3>
                <ul>
                  <li><strong>Best for:</strong> Professional styling</li>
                  <li>Advanced formatting options</li>
                  <li>Multiple fonts and colors</li>
                  <li>Positioning control</li>
                  <li>Popular for anime and fansubs</li>
                </ul>
              </div>

              <div className="feature-card">
                <h3>SBV (YouTube Simple)</h3>
                <ul>
                  <li><strong>Best for:</strong> Quick YouTube upload</li>
                  <li>YouTube-specific format</li>
                  <li>Simple and lightweight</li>
                  <li>Easy to upload directly</li>
                  <li>No complex formatting</li>
                </ul>
              </div>
            </div>

            <div className="process-flow" style={{ marginTop: '30px' }}>
              <div className="process-step">
                <h3>How to Export Subtitles</h3>
                <ol>
                  <li>Open your transcript after transcription is complete</li>
                  <li>Click the <strong>subtitle icon</strong> in the top toolbar (looks like closed captions)</li>
                  <li>Choose your preferred subtitle format (SRT, VTT, ASS, or SBV)</li>
                  <li>The subtitle file will download automatically</li>
                  <li>Use the file with your video player or upload to YouTube</li>
                </ol>
              </div>

              <div className="process-step">
                <h3>Subtitle Features</h3>
                <ul>
                  <li><strong>Automatic Timing:</strong> Perfect sync with your audio using word-level timestamps</li>
                  <li><strong>Smart Line Breaking:</strong> Subtitles automatically broken into readable segments</li>
                  <li><strong>Deleted Words Excluded:</strong> Only active words appear in subtitles</li>
                  <li><strong>Optimal Duration:</strong> Each subtitle lasts 1-7 seconds for readability</li>
                  <li><strong>Standard Compliant:</strong> All exports meet industry subtitle standards</li>
                </ul>
              </div>

              <div className="process-step">
                <h3>Use Cases</h3>
                <ul>
                  <li><strong>Video Editing:</strong> Add captions to your videos in Premiere, Final Cut, etc.</li>
                  <li><strong>YouTube:</strong> Upload as closed captions for accessibility</li>
                  <li><strong>Accessibility:</strong> Meet WCAG compliance requirements</li>
                  <li><strong>Language Learning:</strong> Create subtitles for educational content</li>
                  <li><strong>Podcasts:</strong> Add captions to video podcasts</li>
                  <li><strong>Social Media:</strong> Add captions to Instagram, TikTok, Facebook videos</li>
                </ul>
              </div>
            </div>
          </section>

          <section id="version-history" className="instruction-section">
            <h2>Version History & Auto-Save</h2>
            
            <div className="version-features">
              <div className="feature-card">
                <h3>Automatic Saving</h3>
                <ul>
                  <li>Changes saved every 30 seconds automatically</li>
                  <li>No data loss if browser crashes</li>
                  <li>Auto-save indicator shows save status</li>
                  <li>Manual save option available</li>
                </ul>
              </div>
              
              <div className="feature-card">
                <h3>Version Management</h3>
                <ul>
                  <li>Every significant edit creates a new version</li>
                  <li>View complete history of changes</li>
                  <li>Compare versions side-by-side</li>
                  <li>Restore any previous version</li>
                  <li>Add descriptions to versions</li>
                </ul>
              </div>
              
              <div className="feature-card">
                <h3>Organization</h3>
                <ul>
                  <li>Name your transcripts</li>
                  <li>Add tags for easy sorting</li>
                  <li>Search across all content</li>
                  <li>Organize into folders</li>
                  <li>Mark favorites</li>
                </ul>
              </div>
            </div>
          </section>

          <section id="sharing" className="instruction-section">
            <h2>Sharing & Collaboration</h2>
            
            <div className="sharing-options">
              <div className="share-card">
                <h3>View-Only Links</h3>
                <p>Share read-only versions of your transcripts:</p>
                <ul>
                  <li>Recipients can read and listen</li>
                  <li>No editing permissions</li>
                  <li>Track view count</li>
                  <li>Perfect for sharing final drafts</li>
                </ul>
              </div>
              
              <div className="share-card">
                <h3>Collaborative Editing</h3>
                <p>Allow others to edit your transcripts:</p>
                <ul>
                  <li>Full editing capabilities</li>
                  <li>Changes create new versions</li>
                  <li>See edit history</li>
                  <li>Great for team projects</li>
                </ul>
              </div>
              
              <div className="share-card">
                <h3>Security Options</h3>
                <ul>
                  <li><strong>Password Protection:</strong> Add passwords to sensitive content</li>
                  <li><strong>Expiration Dates:</strong> Set automatic link expiration</li>
                  <li><strong>Revoke Access:</strong> Disable links anytime</li>
                  <li><strong>View Analytics:</strong> See who accessed your content</li>
                </ul>
              </div>
            </div>
          </section>

          <section id="keyboard-shortcuts" className="instruction-section">
            <h2>Keyboard Shortcuts</h2>

            <div className="shortcuts-grid">
              <div className="shortcut-category">
                <h3>Listening Mode Navigation</h3>
                <div className="shortcut-list">
                  <div className="shortcut">
                    <kbd>←</kbd> <kbd>→</kbd>
                    <span>When Bin Navigation disabled: Regular navigation</span>
                  </div>
                  <div className="shortcut">
                    <kbd>←</kbd> <kbd>→</kbd>
                    <span>When Bin Navigation enabled: Navigate within current confidence bin</span>
                  </div>
                  <div className="shortcut">
                    <kbd>Shift</kbd> + <kbd>←</kbd> <kbd>→</kbd>
                    <span>Navigate within confidence bin (when Bin Navigation disabled)</span>
                  </div>
                  <div className="shortcut">
                    <span className="shortcut-note">Click H/M/L counters to jump to confidence bins (when Bin Navigation enabled in settings)</span>
                  </div>
                </div>
              </div>

              <div className="shortcut-category">
                <h3>Typing Mode</h3>
                <div className="shortcut-list">
                  <div className="shortcut">
                    <kbd>←</kbd> <kbd>→</kbd>
                    <span>Move cursor within word or jump to adjacent words</span>
                  </div>
                  <div className="shortcut">
                    <kbd>Ctrl</kbd> + <kbd>←</kbd> <kbd>→</kbd>
                    <span>Jump to previous/next non-deleted word</span>
                  </div>
                  <div className="shortcut">
                    <kbd>Backspace</kbd>
                    <span>Delete character or mark word as deleted</span>
                  </div>
                  <div className="shortcut">
                    <kbd>Delete</kbd>
                    <span>Delete character forward</span>
                  </div>
                  <div className="shortcut">
                    <kbd>Space</kbd>
                    <span>Split word or insert space</span>
                  </div>
                  <div className="shortcut">
                    <kbd>Enter</kbd>
                    <span>Insert new word after selection</span>
                  </div>
                  <div className="shortcut">
                    <kbd>Esc</kbd>
                    <span>Deselect current word</span>
                  </div>
                </div>
              </div>

              <div className="shortcut-category">
                <h3>Undo & Redo</h3>
                <div className="shortcut-list">
                  <div className="shortcut">
                    <kbd>Ctrl</kbd> + <kbd>Z</kbd> <span className="or-text">or</span> <kbd>⌘</kbd> + <kbd>Z</kbd>
                    <span>Undo last change (up to 50 steps)</span>
                  </div>
                  <div className="shortcut">
                    <div><kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>Z</kbd> <span className="or-text">or</span> <kbd>⌘</kbd> + <kbd>Shift</kbd> + <kbd>Z</kbd></div>
                    <span>Redo previously undone change</span>
                  </div>
                  <div className="shortcut">
                    <kbd>Ctrl</kbd> + <kbd>Y</kbd> <span className="or-text">or</span> <kbd>⌘</kbd> + <kbd>Y</kbd>
                    <span>Redo (Windows convention)</span>
                  </div>
                  <div className="shortcut">
                    <span className="shortcut-note">Works for all transcript edits: word changes, deletions, insertions, and replacements</span>
                  </div>
                </div>
              </div>

              <div className="shortcut-category">
                <h3>Audio Playback</h3>
                <div className="shortcut-list">
                  <div className="shortcut">
                    <kbd>Space</kbd>
                    <span>Play/Pause audio (when not in typing mode)</span>
                  </div>
                  <div className="shortcut">
                    <span className="shortcut-note">Use on-screen controls for skip forward/back and speed adjustment</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3>Settings & Features</h3>
              <div className="feature-grid">
                <div className="feature-card">
                  <h4>Confidence Display</h4>
                  <ul>
                    <li><strong>Show Confidence Counter:</strong> Display H/M/L word counts</li>
                    <li><strong>Enable Bin Navigation:</strong> Make counters clickable and enable arrow key navigation within bins</li>
                    <li><strong>Show Confidence %:</strong> Display percentage values on each word</li>
                  </ul>
                </div>
                <div className="feature-card">
                  <h4>Transcript Organization</h4>
                  <ul>
                    <li><strong>Group into Segments:</strong> Organize words into logical segments</li>
                    <li><strong>Hide Deleted Words:</strong> Toggle visibility of deleted/ghosted words</li>
                    <li><strong>Confidence Thresholds:</strong> Adjust the boundary percentages for high/medium/low confidence</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section id="troubleshooting" className="instruction-section">
            <h2>Troubleshooting</h2>
            
            <div className="troubleshooting-grid">
              <div className="problem-solution">
                <h3>Audio Upload Issues</h3>
                <div className="problem">
                  <strong>Problem:</strong> "File format not supported"
                </div>
                <div className="solution">
                  <strong>Solution:</strong> Convert your file to MP3 or WAV format. Use online converters or audio editing software.
                </div>
              </div>
              
              <div className="problem-solution">
                <h3>Poor Transcription Quality</h3>
                <div className="problem">
                  <strong>Problem:</strong> Many red (low-confidence) words
                </div>
                <div className="solution">
                  <strong>Solutions:</strong>
                  <ul>
                    <li>Ensure clear speech with minimal background noise</li>
                    <li>Use higher quality audio recordings</li>
                    <li>Speak at a moderate pace</li>
                    <li>Check microphone positioning</li>
                  </ul>
                </div>
              </div>
              
              <div className="problem-solution">
                <h3>Typing Mode Not Working</h3>
                <div className="problem">
                  <strong>Problem:</strong> Can't edit words in typing mode
                </div>
                <div className="solution">
                  <strong>Solutions:</strong>
                  <ul>
                    <li>Ensure typing mode is enabled (checkbox)</li>
                    <li>Click on the transcript area to focus it</li>
                    <li>Make sure you're clicking on non-ghosted words</li>
                    <li>Try refreshing the page</li>
                  </ul>
                </div>
              </div>
              
              <div className="problem-solution">
                <h3>Audio Sync Issues</h3>
                <div className="problem">
                  <strong>Problem:</strong> Words don't match audio timing
                </div>
                <div className="solution">
                  <strong>Solutions:</strong>
                  <ul>
                    <li>Refresh the page and re-upload the audio</li>
                    <li>Check that the audio file isn't corrupted</li>
                    <li>Ensure stable internet connection during upload</li>
                    <li>Try a different browser</li>
                  </ul>
                </div>
              </div>
              
              <div className="problem-solution">
                <h3>Payment & Subscription</h3>
                <div className="problem">
                  <strong>Problem:</strong> "Active subscription required" error
                </div>
                <div className="solution">
                  <strong>Solutions:</strong>
                  <ul>
                    <li>Check your payment method is valid</li>
                    <li>Visit the payment setup page</li>
                    <li>Contact support if payment was processed</li>
                    <li>Try logging out and back in</li>
                  </ul>
                </div>
              </div>

              <div className="problem-solution">
                <h3>Network/Upload Failures</h3>
                <div className="problem">
                  <strong>Problem:</strong> Upload fails due to poor internet connection
                </div>
                <div className="solution">
                  <strong>Solutions:</strong>
                  <ul>
                    <li>If upload fails, you'll see a dialog with options to download your audio file</li>
                    <li>Click "Download & Retry" to save your audio locally</li>
                    <li>Your audio is never lost - it stays in your browser until uploaded</li>
                    <li>Try uploading again when your connection improves</li>
                    <li>For large files, consider using a wired connection instead of Wi-Fi</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="contact-section">
            <h2>Need More Help?</h2>
            <p>
              If you're still having issues or have questions not covered here, please contact us at <a href="mailto:support@raindragon.ai">support@raindragon.ai</a>. We're here to help you get the most out of Rec-Chord!
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}

export default Instructions;